import { Liquid } from "liquidjs"
import { SegmentArray } from "liquidjs/dist/template"

let engine: Liquid | null = null
let jsonEngine: Liquid | null = null

function jsonStringEscape(value: unknown) {
  if (value === null || value === undefined) return ""
  return JSON.stringify(String(value)).slice(1, -1)
}

function buildEngine(jsonSafe: boolean) {
  // In jsonSafe mode, interpolated values are escaped so they stay valid inside a JSON string
  // literal (e.g. a customer message containing `"` won't break the surrounding payload).
  const instance = jsonSafe ? new Liquid({ outputEscape: jsonStringEscape }) : new Liquid()

  // Override the json filter to properly handle null values. `raw` skips outputEscape so a whole
  // object emitted via `{{ data | json }}` stays valid JSON instead of being string-escaped.
  instance.registerFilter("json", {
    handler: (value: unknown) => JSON.stringify(value, null, 2),
    raw: true,
  })

  return instance
}

function ensureEngine(jsonSafe = false) {
  if (jsonSafe) {
    if (!jsonEngine) jsonEngine = buildEngine(true)
    return jsonEngine
  }
  if (!engine) engine = buildEngine(false)
  return engine
}

function setNestedValue(
  obj: Record<string, unknown>,
  segments: Array<string | number | SegmentArray>,
  value: unknown,
) {
  if (segments.length === 0) return obj

  const [first, ...rest] = segments
  const key = String(first)

  if (rest.length === 0) {
    if (!(key in obj)) {
      const newObj = { ...obj }
      newObj[key] = value
      return newObj
    }
    return obj
  }

  const newObj = { ...obj }
  const currentValue = newObj[key]
  if (
    !(key in newObj) ||
    currentValue === null ||
    typeof currentValue !== "object" ||
    Array.isArray(currentValue)
  ) {
    newObj[key] = {}
  }

  const nestedResult = setNestedValue(newObj[key] as Record<string, unknown>, rest, value)
  if (nestedResult) newObj[key] = nestedResult

  return newObj
}

function getAtPath(data: Record<string, unknown>, segments: Array<string | number | SegmentArray>) {
  let current: unknown = data

  for (const segment of segments) {
    if (current === null || current === undefined) return undefined

    if (Array.isArray(current)) {
      const index = typeof segment === "number" ? segment : Number(String(segment))
      if (Number.isNaN(index)) return undefined
      current = current[index]
      continue
    }

    if (typeof current === "object") {
      current = (current as Record<string, unknown>)[String(segment)]
      continue
    }

    return undefined
  }

  return current
}

// This is a very basic implementation of a safe liquid renderer, where missing variables are set to
// null (and not empty strings, which is the default behavior)
export function renderLiquid(
  template: string,
  data: Record<string, unknown>,
  options: { jsonSafe?: boolean } = {},
) {
  const activeEngine = ensureEngine(options.jsonSafe)

  try {
    const neededVariables = activeEngine.variableSegmentsSync(template)
    let dataWithMissingVariables = { ...data }

    for (const segments of neededVariables) {
      const path = segments as Array<string | number | SegmentArray>
      if (getAtPath(dataWithMissingVariables, path) !== undefined) continue

      const result = setNestedValue(dataWithMissingVariables, path, null)
      if (result) {
        dataWithMissingVariables = result
      }
    }

    return activeEngine.parseAndRenderSync(template, dataWithMissingVariables)
  } catch {
    return template
  }
}
